import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import {
  DvButtonElement,
  DvDropdownClientSideElement,
} from '@devinco/ui/legacy';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';
import { EditTripFilterDialogComponent } from '@shared/components/edit-trip-filter-dialog/edit-trip-filter-dialog.component';
import { EditFilterDialogData } from '@shared/components/edit-trip-filter-dialog/interfaces';
import { map, Observable, take } from 'rxjs';
import {
  DataFilterDropdownItem,
  DataFilterResponse,
} from '@pages/reports-page/components/report-tabs/report-information-tab/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@core/services/dialog.service';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
@Injectable({ providedIn: 'root' })
export class ReportFilterFormService {
  private dialog = inject(DialogService);
  private api = inject(RestApiService);
  private gridEnttiyService = inject(SplitterWithGridHelperService);
  private translate = inject(TranslateService);

  selectedDataFilterID: number | undefined;
  selectedReportEntityId: string;
  filterElements: DvDynamicFormElement[];

  constructor() {
    this.filterElements = this.getElements();
  }

  getElements = (): DvDynamicFormElement[] => [
    new DvDropdownClientSideElement({
      footerFn: this.dataFilterIdActionFooterFn,
      footerText: 'ReportFilterForm.AddFilterFooter',
      colspan: 5,
      validators: [Validators.required],
      key: 'DataFilterID',
      label: 'Report.FilterDescription',
      labelKeys: ['Name'],
      valueKey: 'DataFilterID',
      iconKey: 'IconName',
      iconColorKey: 'IconColor',
      ready: params => {
        const { api, control } = params;
        api.setIsLoading(true);
        this.getDataFilters()
          .pipe(take(1))
          .subscribe(dataFilters => {
            api.setIsLoading(false);
            api.setItems(dataFilters);
            this.filterElements.find(
              el => el.options.id === 'dataFilterEditButton'
            )!.options.disabled = !dataFilters.find(
              dataFilter => dataFilter.DataFilterID === params.control?.value
            )?.Editable;
            control?.markAsTouched();
          });
      },
    }),
    new DvButtonElement({
      id: 'dataFilterEditButton',
      icon: 'edit',
      colspan: 1,
      clickFn: this.dataFilterIdEditClickFn,
    }),
    new DvDropdownClientSideElement({
      isClearable: true,
      colspan: 6,
      key: 'ReportTypeID',
      label: 'Report.ReportType',
      labelKeys: ['ReportType'],
      valueKey: 'ReportTypeID',
      labelKeysSeparator: '',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.ReportType,
          fields: ['ReportTypeID', 'Name'],
        };
        this.api.setTranslatedDropdownData(
          request,
          params,
          'Name',
          'ReportType'
        );
      },
      validators: [Validators.required],
    }),
  ];

  getDataFilters(): Observable<DataFilterDropdownItem[]> {
    return this.api
      .getData({
        entity: Entity.DataFilter,
        fields: ['DataFilterID', 'Name', 'Editable', 'TranslationKey'],
        // needed to show filters of all users)
        customFlag: true,
      } as GetRowsRequest)
      .pipe(
        map(resp =>
          (resp.data as DataFilterResponse[]).map(dataFilter => ({
            ...dataFilter,
            Name: dataFilter.TranslationKey
              ? this.translate.instant(dataFilter.TranslationKey)
              : dataFilter.Name,
            IconName: dataFilter.Editable ? '' : 'lock',
            IconColor: 'grey',
          }))
        )
      );
  }

  toggleFilterEditButton(dataFilterID: number): void {
    const selectedItem: DataFilterDropdownItem | undefined = (
      this.filterElements.find(
        el => el.options.key === 'DataFilterID'
      )! as DvDropdownClientSideElement
    ).options.api
      ?.getItems()
      .find(item => item.DataFilterID === dataFilterID);

    this.filterElements.find(
      el => el.options.id === 'dataFilterEditButton'
    )!.options.disabled = !selectedItem?.Editable;
  }

  private dataFilterIdActionFooterFn = (): void => {
    this.dialog
      .open<EditFilterDialogData, EditTripFilterDialogComponent>({
        size: 'full',
        contentComponent: EditTripFilterDialogComponent,
        title: 'ReportFilter.Create',
        confirmText: 'General.Confirm',
        hideFooter: true,
        cancelText: 'General.Cancel',
        data: { entityData: { DataFilterID: undefined } },
      })
      .closed.pipe(take(1))
      .subscribe(({ source }) => {
        if (source === 'confirm') {
          this.getDataFilters().subscribe(dataFilters => {
            (
              this.filterElements.find(
                el => el.options.key === 'DataFilterID'
              )! as DvDropdownClientSideElement
            ).options.api?.setItems(dataFilters);
          });
        }
      });
  };

  private dataFilterIdEditClickFn = (): void => {
    this.dialog
      .open<EditFilterDialogData, EditTripFilterDialogComponent>({
        size: 'full',
        contentComponent: EditTripFilterDialogComponent,
        title: 'ReportFilter.Edit',
        confirmText: 'General.Confirm',
        cancelText: 'General.Cancel',
        hideFooter: true,
        data: {
          entityData: {
            DataFilterID: this.selectedDataFilterID,
          },
        },
      })
      .closed.pipe(take(1))
      .subscribe(event => {
        if (event.source === 'confirm') {
          this.gridEnttiyService.refreshRowData(this.selectedReportEntityId);
          if (event.responseData.hasFilterNameChanged) {
            this.getDataFilters().subscribe(dataFilters => {
              const dataFilterElementOpt = (
                this.filterElements.find(
                  el => el.options.key === 'DataFilterID'
                )! as DvDropdownClientSideElement
              ).options;
              dataFilterElementOpt.api?.setItems(dataFilters);
            });
          }
        }
      });
  };
}
